import { useContext, useEffect, useState } from "react";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { CurrentCompanyStorage, mapGroupCompanyOptions } from "../../library/core/current-company";
import { CompanySelectInput } from "../form/company-dropdown";

// Design `.main`: 96px sidebar offset, left-aligned, max-width 980, 36px padding
const mainWrap =
  "max-w-[980px] min-[880px]:ml-24 px-[12px] pt-[18px] pb-24 min-[880px]:px-9 min-[880px]:pt-7 min-[880px]:pb-[60px]";

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18" />
    <path d="M12 3a14 14 0 0 0 0 18" />
  </svg>
);

const FileEditIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5a2 2 0 0 1 2-2h8l6 6v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h5" />
    <path d="M8 17h8" />
  </svg>
);

const FileListIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8" />
    <path d="M8 12h8" />
    <path d="M8 16h5" />
  </svg>
);

const REVIEW_LINK = {
  title: "Review my venue listing",
  sub: "See how customers see your venue on the directory",
  href: null,
  icon: <GlobeIcon />,
};

const LINKS_BY_VENDOR = {
  BR: [
    {
      title: "Update my venue listing",
      sub: "Edit details, hours, photos and contact info",
      href: "https://share.hsforms.com/1OuyTXsGXQXaADtrFhGolpQcvwgd",
      icon: <FileEditIcon />,
    },
    {
      title: "Promote specials and experiences at my venue",
      sub: "Add a promotion that appears on your listing",
      href: "https://cvwgd.share.hsforms.com/2kKPaFc11Sv-bjSHRk46WDg",
      icon: <FileListIcon />,
    },
  ],
  BB: [
    {
      title: "Update my venue listing",
      sub: "Edit details, hours, photos and contact info",
      href: "https://share.hsforms.com/1A8ZuVz4FSLyF6FmcLeYLugcvwgd",
      icon: <FileEditIcon />,
    },
    {
      title: "Promote specials and experiences at my venue",
      sub: "Add a promotion that appears on your listing",
      href: "https://share.hsforms.com/1N2Xk-sYSTtmn6JtzYIn5RAcvwgd",
      icon: <FileListIcon />,
    },
  ],
  BW: [
    {
      title: "Update my venue listing",
      sub: "Edit details, hours, photos and contact info",
      href: "https://share.hsforms.com/15hHSFwDXQSugb-yA0sM5Pwcvwgd",
      icon: <FileEditIcon />,
    },
    {
      title: "Promote specials and experiences at my venue",
      sub: "Add a promotion that appears on your listing",
      href: "https://share.hsforms.com/1B1xkKkj4QoSUU_yfdTvjvwcvwgd",
      icon: <FileListIcon />,
    },
  ],
};

export const VenueListing = () => {
  const { state } = useContext(CognitoContext);
  const [selectedCompany, setSelectedCompany] = useState(() => ({
    companyId: CurrentCompanyStorage.getCurrentCompanyId(),
  }));
  const [companyOptions, setCompanyOptions] = useState({});
  const [selectedVendorType, setSelectedVendorType] = useState(null);
  const [selectedWebpageUrl, setSelectedWebpageUrl] = useState(null);

  useEffect(() => {
    if (!state.userInfo) return;
    const { userInfo } = state;
    const storedId = CurrentCompanyStorage.getCurrentCompanyId();
    const hasStored = userInfo.GroupCompanies.some(
      (m) => `${m.Id}` === `${storedId}`
    );
    const preferredId = hasStored ? storedId : userInfo.CompanyId;

    setSelectedCompany({ companyId: preferredId });
    const options = mapGroupCompanyOptions(userInfo.GroupCompanies);
    setCompanyOptions({
      options,
      defaultCompanyId: preferredId,
      readOnly: !userInfo.HasFullGroupAccess || userInfo.GroupCompanies.length < 2,
    });
    const preferredCompany = options.find((o) => String(o.id) === String(preferredId));
    setSelectedVendorType(preferredCompany?.vendorType ?? null);
    setSelectedWebpageUrl(preferredCompany?.webpageUrl ?? null);

    CurrentCompanyStorage.saveCurrentCompanyId(preferredId);
  }, [state]);

  const onSetTargetCompany = (companyId) => {
    CurrentCompanyStorage.saveCurrentCompanyId(companyId);
    setSelectedCompany({ companyId });
    const match = companyOptions.options?.find((o) => String(o.id) === String(companyId));
    setSelectedVendorType(match?.vendorType ?? null);
    setSelectedWebpageUrl(match?.webpageUrl ?? null);
  };

  const links = [
    ...(selectedWebpageUrl ? [{ ...REVIEW_LINK, href: selectedWebpageUrl }] : []),
    ...(LINKS_BY_VENDOR[
      Object.keys(LINKS_BY_VENDOR).find((k) => selectedVendorType?.startsWith(k))
    ] ?? LINKS_BY_VENDOR.BR),
  ];

  return (
    <div className="overflow-x-auto text-white">
      <div className={mainWrap}>
        {/* Venue switcher (functional dropdown, inline) */}
        <div className="mb-[22px] mx-[4px]">
          <CompanySelectInput
            dataModel={selectedCompany}
            {...companyOptions}
            onChange={onSetTargetCompany}
            customClassName="!rounded-2xl"
          />
        </div>

        {/* Topbar */}
        <div className="flex items-center gap-[12px] mt-[4px] mb-[22px] mx-[4px]">
          <h1 className="m-0 text-[26px] font-bold font-headings-primary tracking-[-0.01em] text-white">
            Promote
          </h1>
        </div>

        {/* Card + drill list */}
        <div className="bg-[#14141d] border-[1px] border-solid border-[#20202c] rounded-2xl px-[22px] py-[18px]">
          <div className="drill-list">
            {links.map((l) => {
              const Comp = l.href ? "a" : "button";
              const extraProps = l.href
                ? { href: l.href, target: "_blank", rel: "noopener noreferrer" }
                : { type: "button" };
              return (
                <Comp key={l.title} {...extraProps} className="drill-link">
                  <span>
                    {l.title}
                    <div className="drill-sub">{l.sub}</div>
                  </span>
                  <span className="chev-sm">{l.icon}</span>
                </Comp>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
